import React, { useContext } from "react";
import { connect } from "react-redux";
import { useGoogleLogin } from "react-use-googlelogin";
import { signInRed, signOutRed } from "../store/actions/Actions";
import DEFAULT_CONFIG from "./config";

/**
 * Generate a context for Authentication Provider component.
 */
const createContext = () => {
  const ctx = React.createContext();
  const useCtx = () => {
    const contextValue = useContext(ctx);

    if (contextValue === undefined) {
      throw new Error("useCtx must be inside a Provider with a value");
    }

    return contextValue;
  };

  return [useCtx, ctx.Provider];
};
const [useAuth, Auth] = createContext();

const SAMPLE_GOOGLE_CLIENT_ID = "Sample Id";
const DEFAULT_IDP = "ras";

const isConfiguredGoogleClientId = (clientId) =>
  typeof clientId === "string" &&
  clientId.trim() !== "" &&
  clientId.trim() !== SAMPLE_GOOGLE_CLIENT_ID;

const GoogleLoginState = ({ clientId, children }) => {
  const googleAuth = useGoogleLogin({
    clientId,
  });

  // what does this return?
  return children(googleAuth);
};

const googleNotConfigured = () =>
  Promise.reject(new Error("Google login is not configured."));

const PLACEHOLDER_ENV_PATTERN = /^\$\{[^}]+\}$/;

const isUsableAbsoluteUrl = (value) => {
  if (typeof value !== "string") {
    return false;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue || PLACEHOLDER_ENV_PATTERN.test(trimmedValue)) {
    return false;
  }

  try {
    const parsedUrl = new URL(trimmedValue);
    return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
  } catch {
    return false;
  }
};

export const getRasLogoutRedirectUrl = (logoutUrl = "") =>
  isUsableAbsoluteUrl(logoutUrl) ? logoutUrl.trim() : "";

const isRasIdp = (IDP) =>
  typeof IDP === "string" && IDP.trim().toLowerCase() === "ras";

const getRequestedIdp = (IDP) =>
  typeof IDP === "string" && IDP.trim() !== ""
    ? IDP.trim().toLowerCase()
    : DEFAULT_IDP;

const LOGOUT_SUCCESS_STORAGE_KEY = "showLogoutSuccess";

const createLogoutResult = (externalLogoutStarted = false) => ({
  logoutCompleted: true,
  externalLogoutStarted,
});

const createLogoutFailureResult = (error) => ({
  logoutCompleted: false,
  externalLogoutStarted: false,
  errorMessage:
    error && error.message
      ? error.message
      : "Unable to complete logout. Please try again.",
});

const canRedirectLocally = (history) =>
  history && typeof history.push === "function";

const createLogoutResponseError = (logoutResponse) => {
  const statusCode = logoutResponse ? logoutResponse.status : "unknown";
  return new Error(`Unable to complete logout. Auth service returned status code ${statusCode}.`);
};

/**
 * Generate a Authentication Provider component with the custom configuration applied
 *
 * @param {object} [uiConfig]
 * @returns {object} { AuthProvider }
 */
export const AuthProviderGenerator = (uiConfig = DEFAULT_CONFIG) => {
  const { config, functions } = uiConfig;

  const redirect =
    functions && typeof functions.redirect === "function"
      ? functions.redirect
      : DEFAULT_CONFIG.functions.redirect;

  const storeInLocalStorage =
    functions && typeof functions.storeInLocalStorage === "function"
      ? functions.storeInLocalStorage
      : DEFAULT_CONFIG.functions.storeInLocalStorage;

  const deleteFromLocalStorage =
    functions && typeof functions.deleteFromLocalStorage === "function"
      ? functions.deleteFromLocalStorage
      : DEFAULT_CONFIG.functions.deleteFromLocalStorage;

  const GOOGLE_CLIENT_ID =
    config && typeof config.GOOGLE_CLIENT_ID === "string"
      ? config.GOOGLE_CLIENT_ID
      : DEFAULT_CONFIG.config.GOOGLE_CLIENT_ID;

  const NIH_CLIENT_ID =
    config && typeof config.NIH_CLIENT_ID === "string"
      ? config.NIH_CLIENT_ID
      : DEFAULT_CONFIG.config.NIH_CLIENT_ID;

  const NIH_AUTH_URL =
    config && typeof config.NIH_AUTH_URL === "string"
      ? config.NIH_AUTH_URL
      : DEFAULT_CONFIG.config.NIH_AUTH_URL;

  const AUTH_API =
    config && typeof config.AUTH_API === "string"
      ? config.AUTH_API
      : DEFAULT_CONFIG.config.AUTH_API;

  const AUTH_URL =
    config && typeof config.AUTH_URL === "string"
      ? config.AUTH_URL
      : DEFAULT_CONFIG.config.AUTH_URL;

  const RAS_BROWSER_LOGOUT_URL =
    config && typeof config.RAS_BROWSER_LOGOUT_URL === "string"
      ? config.RAS_BROWSER_LOGOUT_URL
      : DEFAULT_CONFIG.config.RAS_BROWSER_LOGOUT_URL;

  const stateProps = () => ({
    // autocomplete: state.login.autocomplete,
  });

  const dispatchProps = (dispatch) => ({
    signIn: (data) => dispatch(signInRed(data)),
    signOut: () => dispatch(signOutRed()),
  });

  return {
    // @ts-ignore
    // eslint-disable-next-line max-len
    AuthProvider: connect(
      stateProps,
      dispatchProps,
    )((props) => {
      const { children, signIn, signOut } = props;
      const originDomain = window.location.origin;
      const googleLoginEnabled = isConfiguredGoogleClientId(GOOGLE_CLIENT_ID);

      const renderAuthProvider = ({
        googleUser = null,
        isInitialized = !googleLoginEnabled,
        grantOfflineAccess = googleNotConfigured,
        isSignedIn = false,
      } = {}) => {
        async function authServiceLogin(
          code,
          IDP = DEFAULT_IDP,
          redirectUri,
          signInSuccess = () => {},
          signInError = () => {},
        ) {
          try {
            const requestedIdp = getRequestedIdp(IDP);
            const rawResponse = await fetch(`${AUTH_API}login`, {
              method: "POST",
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ code, IDP: requestedIdp, redirectUri }),
            });

            if (!rawResponse || typeof rawResponse.json !== "function") {
              signInError("Error fetching user data.");
              return;
            }

            let responseData;
            try {
              responseData = await rawResponse.json();
            } catch (error) {
              signInError("Error fetching user data.");
              return;
            }

            if (rawResponse.status === 200) {
              const userDetails = {
                ...responseData,
                IDP: responseData.IDP || responseData.idp || requestedIdp,
              };
              signIn(userDetails);
              storeInLocalStorage("userDetails", userDetails);
              signInSuccess(userDetails);
            } else if (
              rawResponse.status === 400 ||
              rawResponse.status === 403
            ) {
              signInError("Error fetching user data.");
            } else {
              signInError("Internal Error");
            }
          } catch (error) {
            signInError("Error fetching user data.");
          }
        }

        const signInWithGoogle = (success = () => {}, error = () => {}) => {
          grantOfflineAccess()
            .then((resp) => {
              if (resp) {
                // Send the code to auth service
                authServiceLogin(resp, "google", originDomain, success, error);
              } else {
                error();
              }
            })
            .catch((err) => {
              console.warn("[Google login] Unable to start Google login", {
                configured: googleLoginEnabled,
                message: err && err.message,
              });
              error(err);
            });
        };

        const signInWithNIH = (state) => {
          const urlParam = {
            client_id: NIH_CLIENT_ID,
            redirect_uri: `${originDomain}/nihloginsuccess`,
            response_type: "code",
            scope: "openid email profile",
            state: JSON.stringify(state || {}),
          };

          const params = new URLSearchParams(urlParam).toString();
          window.location.href = `${NIH_AUTH_URL}?${params}`;
        };

        const signInWithAuthURL = (state) => {
          window.location.href = `${AUTH_URL}`;
        };

        const onSignOut = async (history, redirectPath = "/", IDP = DEFAULT_IDP) => {
          const requestedIdp = getRequestedIdp(IDP);
          const isRasLogout = isRasIdp(requestedIdp);
          try {
            const logoutResponse = await fetch(`${AUTH_API}logout`, {
              method: "POST",
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ IDP: requestedIdp }),
            });

            if (!logoutResponse || !logoutResponse.ok) {
              throw createLogoutResponseError(logoutResponse);
            }
          } catch (error) {
            console.warn("[Auth logout] Unable to call Auth service logout", {
              message: error && error.message,
            });
            return createLogoutFailureResult(error);
          }

          deleteFromLocalStorage("userDetails");
          signOut();
          dispatchProps("signOut");

          const rasLogoutRedirectUrl = isRasLogout
            ? getRasLogoutRedirectUrl(RAS_BROWSER_LOGOUT_URL)
            : "";

          if (rasLogoutRedirectUrl) {
            try {
              sessionStorage.setItem(LOGOUT_SUCCESS_STORAGE_KEY, "true");
            } catch (error) {
              console.warn("[RAS logout] Unable to save return notification flag", {
                message: error && error.message,
              });
            }
            // Browser navigation is required so RAS can clear its SSO cookies.
            window.location.assign(rasLogoutRedirectUrl);
            return createLogoutResult(true);
          }

          if (canRedirectLocally(history)) {
            redirect(history, redirectPath);
          }
          return createLogoutResult(false);
        };

        return (
          <Auth
            value={{
              signInWithGoogle,
              signInWithNIH,
              signInWithAuthURL,
              isSignedIn,
              isInitialized,
              googleUser,
              signOut: onSignOut,
              authServiceLogin,
              // fetchWithRefresh,
            }}
          >
            {children}
          </Auth>
        );
      };

      if (!googleLoginEnabled) {
        return renderAuthProvider();
      }

      return (
        <GoogleLoginState clientId={GOOGLE_CLIENT_ID}>
          {renderAuthProvider}
        </GoogleLoginState>
      );
    }),
  };
};

export default AuthProviderGenerator;
export { useAuth };
